import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Ticket,
    Loader2,
    Save,
    Trash2,
    Plus,
    Copy,
    Eye,
    EyeOff,
    Calendar,
    Users,
    Coins,
    RefreshCw,
    Gift
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Coupon {
    id: string;
    code: string;
    credits_amount: number;
    max_uses: number | null;
    current_uses: number;
    max_per_user: number;
    expires_at: string | null;
    is_active: boolean;
    description: string | null;
    created_at: string;
    updated_at: string;
}

const CouponManagement = () => {
    const { toast } = useToast();
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Welcome Credits state
    const [welcomeCreditsEnabled, setWelcomeCreditsEnabled] = useState(false);
    const [welcomeCreditsAmount, setWelcomeCreditsAmount] = useState(100);
    const [isLoadingWelcome, setIsLoadingWelcome] = useState(true);
    const [isSavingWelcome, setIsSavingWelcome] = useState(false);

    // Form state
    const [editId, setEditId] = useState<string | null>(null);
    const [code, setCode] = useState("");
    const [creditsAmount, setCreditsAmount] = useState(100);
    const [maxUses, setMaxUses] = useState<string>("");
    const [maxPerUser, setMaxPerUser] = useState(1);
    const [expiresAt, setExpiresAt] = useState("");
    const [description, setDescription] = useState("");
    const [isActive, setIsActive] = useState(true);

    // Fetch welcome credits settings
    const fetchWelcomeSettings = async () => {
        setIsLoadingWelcome(true);
        try {
            const { data, error } = await (supabase as any)
                .from("system_settings")
                .select("key, value")
                .in("key", ["welcome_credits_enabled", "welcome_credits_amount"]);

            if (!error && data) {
                data.forEach((setting: { key: string; value: string }) => {
                    if (setting.key === "welcome_credits_enabled") {
                        setWelcomeCreditsEnabled(setting.value === "true");
                    }
                    if (setting.key === "welcome_credits_amount") {
                        setWelcomeCreditsAmount(parseInt(setting.value) || 100);
                    }
                });
            }
        } catch (err) {
            console.error("Error fetching welcome settings:", err);
        } finally {
            setIsLoadingWelcome(false);
        }
    };

    // Toggle welcome credits
    const handleToggleWelcomeCredits = async (enabled: boolean) => {
        setIsSavingWelcome(true);
        try {
            const { error } = await (supabase as any)
                .from("system_settings")
                .update({ value: enabled ? "true" : "false" })
                .eq("key", "welcome_credits_enabled");

            if (error) throw error;
            setWelcomeCreditsEnabled(enabled);
            toast({
                title: enabled ? "เปิดเครดิตฟรีสมัครใหม่" : "ปิดเครดิตฟรีสมัครใหม่",
                description: enabled
                    ? `ผู้ใช้ใหม่จะได้รับ ${welcomeCreditsAmount} เครดิตฟรี`
                    : "ผู้ใช้ใหม่จะไม่ได้รับเครดิตฟรี",
            });
        } catch (err: any) {
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        } finally {
            setIsSavingWelcome(false);
        }
    };

    const fetchCoupons = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await (supabase as any)
                .from("coupons")
                .select("*")
                .order("created_at", { ascending: false });

            if (error) throw error;
            setCoupons(data || []);
        } catch (err: any) {
            console.error("Error fetching coupons:", err);
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupons();
        fetchWelcomeSettings();
    }, []);

    const resetForm = () => {
        setEditId(null);
        setCode("");
        setCreditsAmount(100);
        setMaxUses("");
        setMaxPerUser(1);
        setExpiresAt("");
        setDescription("");
        setIsActive(true);
    };

    const handleEdit = (coupon: Coupon) => {
        setEditId(coupon.id);
        setCode(coupon.code);
        setCreditsAmount(coupon.credits_amount);
        setMaxUses(coupon.max_uses?.toString() || "");
        setMaxPerUser(coupon.max_per_user);
        setExpiresAt(coupon.expires_at ? coupon.expires_at.slice(0, 16) : "");
        setDescription(coupon.description || "");
        setIsActive(coupon.is_active);
    };

    const generateRandomCode = () => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let result = "";
        for (let i = 0; i < 8; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setCode(result);
    };

    const handleSave = async () => {
        if (!code.trim()) {
            toast({
                title: "Error",
                description: "กรุณากรอกรหัสคูปอง",
                variant: "destructive",
            });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                code: code.toUpperCase().trim(),
                credits_amount: creditsAmount,
                max_uses: maxUses ? parseInt(maxUses) : null,
                max_per_user: maxPerUser,
                expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
                description: description.trim() || null,
                is_active: isActive,
            };

            if (editId) {
                const { error } = await (supabase as any)
                    .from("coupons")
                    .update(payload)
                    .eq("id", editId);

                if (error) throw error;
                toast({ title: "บันทึกสำเร็จ", description: "อัปเดตคูปองเรียบร้อยแล้ว" });
            } else {
                const { error } = await (supabase as any)
                    .from("coupons")
                    .insert(payload);

                if (error) {
                    if (error.code === "23505") {
                        throw new Error("รหัสคูปองนี้ถูกใช้ไปแล้ว");
                    }
                    throw error;
                }
                toast({ title: "สร้างสำเร็จ", description: "สร้างคูปองใหม่เรียบร้อยแล้ว" });
            }

            resetForm();
            fetchCoupons();
        } catch (err: any) {
            console.error("Error saving coupon:", err);
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm("คุณแน่ใจหรือไม่ที่จะลบคูปองนี้?")) return;

        try {
            const { error } = await (supabase as any)
                .from("coupons")
                .delete()
                .eq("id", id);

            if (error) throw error;
            toast({ title: "ลบสำเร็จ", description: "ลบคูปองเรียบร้อยแล้ว" });
            fetchCoupons();
            if (editId === id) resetForm();
        } catch (err: any) {
            console.error("Error deleting coupon:", err);
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        }
    };

    const handleToggleActive = async (id: string, currentStatus: boolean) => {
        try {
            const { error } = await (supabase as any)
                .from("coupons")
                .update({ is_active: !currentStatus })
                .eq("id", id);

            if (error) throw error;
            toast({
                title: !currentStatus ? "เปิดใช้งานคูปอง" : "ปิดใช้งานคูปอง",
            });
            fetchCoupons();
        } catch (err: any) {
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({ title: "คัดลอกแล้ว", description: text });
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString || "").toLocaleDateString("th-TH", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const isExpired = (expiresAt: string | null) => {
        if (!expiresAt) return false;
        return new Date(expiresAt) < new Date();
    };

    return (
        <div className="space-y-6">
            {/* Welcome Credits Toggle */}
            <Card variant="glass" className="border-primary/20">
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center">
                                <Gift className="h-6 w-6 text-green-500" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">เครดิตฟรีสมัครใหม่</h3>
                                <p className="text-sm text-muted-foreground">
                                    ผู้ใช้ใหม่ที่สมัครจะได้รับ {welcomeCreditsAmount} เครดิตฟรีอัตโนมัติ
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {isLoadingWelcome ? (
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            ) : (
                                <>
                                    <span className={`text-sm font-medium ${welcomeCreditsEnabled ? 'text-green-500' : 'text-muted-foreground'}`}>
                                        {welcomeCreditsEnabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                                    </span>
                                    <Switch
                                        checked={welcomeCreditsEnabled}
                                        onCheckedChange={handleToggleWelcomeCredits}
                                        disabled={isSavingWelcome}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Form Card */}
            <Card variant="glass">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Ticket className="h-5 w-5" />
                        {editId ? "แก้ไขคูปอง" : "สร้างคูปองใหม่"}
                    </CardTitle>
                    <CardDescription>
                        สร้างรหัสโปรโมชั่นสำหรับแจกเครดิตฟรี ใช้ที่หน้า /promo
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="code">รหัสคูปอง *</Label>
                            <div className="flex gap-2">
                                <Input
                                    id="code"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    placeholder="เช่น NEWYEAR2026"
                                    className="uppercase"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    onClick={generateRandomCode}
                                    title="สุ่มรหัส"
                                >
                                    <RefreshCw className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="credits">จำนวนเครดิต *</Label>
                            <Input
                                id="credits"
                                type="number"
                                value={creditsAmount}
                                onChange={(e) => setCreditsAmount(parseInt(e.target.value) || 0)}
                                min={1}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="maxUses">จำนวนครั้งที่ใช้ได้ (ว่าง = ไม่จำกัด)</Label>
                            <Input
                                id="maxUses"
                                type="number"
                                value={maxUses}
                                onChange={(e) => setMaxUses(e.target.value)}
                                placeholder="ไม่จำกัด"
                                min={1}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="maxPerUser">ใช้ได้กี่ครั้งต่อคน</Label>
                            <Input
                                id="maxPerUser"
                                type="number"
                                value={maxPerUser}
                                onChange={(e) => setMaxPerUser(parseInt(e.target.value) || 1)}
                                min={1}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="expiresAt">วันหมดอายุ</Label>
                            <Input
                                id="expiresAt"
                                type="datetime-local"
                                value={expiresAt}
                                onChange={(e) => setExpiresAt(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="description">หมายเหตุ (สำหรับ Admin)</Label>
                            <Input
                                id="description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="เช่น แจกลูกค้าใหม่ ม.ค. 2026"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Switch
                            checked={isActive}
                            onCheckedChange={setIsActive}
                        />
                        <span className={isActive ? "text-green-500 font-medium" : "text-muted-foreground"}>
                            {isActive ? "เปิดใช้งาน" : "ปิดใช้งาน"}
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button onClick={handleSave} disabled={isSaving} className="gap-2">
                            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {editId ? "บันทึกการแก้ไข" : "สร้างคูปอง"}
                        </Button>
                        {editId && (
                            <Button variant="outline" onClick={resetForm}>
                                ยกเลิก
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Coupons List */}
            <Card variant="glass">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>รายการคูปองทั้งหมด ({coupons.length})</CardTitle>
                    <Button variant="ghost" size="sm" onClick={fetchCoupons} disabled={isLoading}>
                        <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </Button>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : coupons.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Ticket className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>ยังไม่มีคูปอง</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {coupons.map((coupon) => (
                                <div
                                    key={coupon.id}
                                    className={`flex flex-col md:flex-row md:items-center justify-between p-4 rounded-xl border transition-colors ${coupon.is_active && !isExpired(coupon.expires_at)
                                        ? "bg-muted/30 hover:bg-muted/50 border-border/50"
                                        : "bg-red-500/5 border-red-500/20 opacity-70"
                                        }`}
                                >
                                    <div className="flex items-start gap-4 flex-1">
                                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                            <Ticket className="h-6 w-6 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <code className="text-lg font-bold font-mono">
                                                    {coupon.code}
                                                </code>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6"
                                                    onClick={() => copyToClipboard(coupon.code)}
                                                >
                                                    <Copy className="h-3 w-3" />
                                                </Button>
                                                {!coupon.is_active && (
                                                    <Badge variant="destructive">ปิดใช้งาน</Badge>
                                                )}
                                                {isExpired(coupon.expires_at) && (
                                                    <Badge variant="destructive">หมดอายุ</Badge>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                                                <span className="flex items-center gap-1">
                                                    <Coins className="h-3 w-3" />
                                                    {coupon.credits_amount} เครดิต
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Users className="h-3 w-3" />
                                                    ใช้แล้ว {coupon.current_uses}/{coupon.max_uses || "∞"}
                                                </span>
                                                {coupon.expires_at && (
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="h-3 w-3" />
                                                        หมดอายุ {formatDate(coupon.expires_at)}
                                                    </span>
                                                )}
                                            </div>
                                            {coupon.description && (
                                                <p className="text-xs text-muted-foreground mt-1 italic">
                                                    {coupon.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 mt-3 md:mt-0 shrink-0">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleToggleActive(coupon.id, coupon.is_active)}
                                            className={coupon.is_active ? "text-green-500" : "text-muted-foreground"}
                                        >
                                            {coupon.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleEdit(coupon)}
                                        >
                                            แก้ไข
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDelete(coupon.id)}
                                            className="text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default CouponManagement;
